/**
 * Image paste / drop → upload to the vault's attachments folder → insert `![[path]]`.
 */
import { EditorView } from '@codemirror/view';
import { Api } from '../../lib/api';

function insertAt(view: EditorView, pos: number, text: string) {
  view.dispatch({ changes: { from: pos, insert: text }, selection: { anchor: pos + text.length } });
}

async function handleFiles(view: EditorView, files: File[], notePath: string | undefined, pos: number, notify: (msg: string, kind?: 'info' | 'warn' | 'crit') => void) {
  for (const f of files) {
    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
    const name = f.name && f.name !== 'image.png' ? f.name : `pasted-${stamp}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`;
    const placeholder = `![[uploading ${name}…]]`;
    insertAt(view, pos, placeholder);
    try {
      const res = await Api.attachments.upload(name, f, notePath);
      const cur = view.state.doc.toString();
      const at = cur.indexOf(placeholder);
      const link = f.type.startsWith('image/') ? `![[${res.path}]]` : `[[${res.path}]]`;
      if (at >= 0) view.dispatch({ changes: { from: at, to: at + placeholder.length, insert: link } });
      notify(`Attached ${res.path} (${(res.bytes / 1024).toFixed(0)} KB)`);
    } catch (err) {
      const cur = view.state.doc.toString();
      const at = cur.indexOf(placeholder);
      if (at >= 0) view.dispatch({ changes: { from: at, to: at + placeholder.length, insert: '' } });
      notify(`Upload failed: ${(err as Error).message}`, 'crit');
    }
  }
}

export function attachmentPaste(getNotePath: () => string | undefined, notify: (msg: string, kind?: 'info' | 'warn' | 'crit') => void) {
  return EditorView.domEventHandlers({
    paste(e, view) {
      const files = [...(e.clipboardData?.files ?? [])];
      if (!files.length) return false;
      e.preventDefault();
      void handleFiles(view, files, getNotePath(), view.state.selection.main.head, notify);
      return true;
    },
    drop(e, view) {
      const files = [...(e.dataTransfer?.files ?? [])];
      if (!files.length) return false;
      e.preventDefault();
      const pos = view.posAtCoords({ x: e.clientX, y: e.clientY }) ?? view.state.selection.main.head;
      void handleFiles(view, files, getNotePath(), pos, notify);
      return true;
    },
  });
}
